/**
 * Reusable audit-event timeline. Used on the Dashboard's "Today" strip and
 * on the dedicated /activity page. Polls every 10s — fine for now; a future
 * v1.1 can stream audit events over the existing WS.
 *
 * Each event gets a one-line summary derived from `action` + `target_type` +
 * the actor's display name. The full payload is available in the diff dropdown
 * on the /activity page (controlled via `expanded` prop on the row).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { auditApi, type AuditEvent } from "../api/queries/companies";
import { formatRelative, formatDate } from "../lib/time";

export interface ActivityFeedProps {
  companyId: string;
  /** Hard cap on number of events fetched. */
  limit?: number;
  /** Filter to a single action prefix (e.g. "user." or "device.pair_init"). */
  actionFilter?: string;
  /** Filter to events generated by one user id. */
  actorFilter?: string;
  /** Show a "View all" link to /activity when true (used on Dashboard strip). */
  showViewAll?: boolean;
  /** Allow each row to expand its diff. */
  rowsExpandable?: boolean;
  /** Empty-state copy. */
  emptyMessage?: string;
}

export function ActivityFeed({
  companyId,
  limit = 20,
  actionFilter,
  actorFilter,
  rowsExpandable = false,
  emptyMessage = "No activity yet.",
}: ActivityFeedProps) {
  const q = useQuery({
    queryKey: ["audit", companyId, limit, actionFilter, actorFilter],
    queryFn: () =>
      auditApi.list(companyId, {
        limit,
        action: actionFilter,
        actor: actorFilter,
      }),
    refetchInterval: 10_000,
  });

  if (q.isLoading) {
    return (
      <div className="grid place-items-center py-4">
        <span className="spinner" aria-hidden="true" />
      </div>
    );
  }
  const events = q.data?.events ?? [];
  if (events.length === 0) {
    return <div className="text-[12px] text-tertiary px-1 py-2">{emptyMessage}</div>;
  }

  return (
    <ul className="divide-y divide-border-subtle">
      {events.map((e) => (
        <ActivityRow key={e.id} event={e} expandable={rowsExpandable} />
      ))}
    </ul>
  );
}

function ActivityRow({ event, expandable }: { event: AuditEvent; expandable: boolean }) {
  const [open, setOpen] = useState(false);
  const { verb, object, tone } = describeEvent(event);
  const actor =
    event.actor_display_name?.trim() ||
    event.actor_email?.trim() ||
    "system";

  return (
    <li className="py-2 px-1 text-[12px]">
      <div className="flex items-baseline gap-2">
        {expandable && (
          <button
            type="button"
            aria-label={open ? "Collapse" : "Expand"}
            className="iconbtn"
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronRight
              size={12}
              className={`transition-transform ${open ? "rotate-90" : ""}`}
            />
          </button>
        )}
        <span
          className={`shrink-0 inline-block w-1.5 h-1.5 rounded-full ${tone}`}
          aria-hidden="true"
        />
        <span className="text-secondary truncate">
          <strong className="text-primary">{actor}</strong> {verb}{" "}
          <span className="text-tertiary">{object}</span>
        </span>
        <span
          className="ml-auto shrink-0 text-tertiary font-mono"
          title={formatDate(event.at)}
        >
          {formatRelative(event.at)}
        </span>
      </div>
      {expandable && open && (
        <div className="mt-2 ml-6 rounded-sm border border-border-subtle bg-sunken px-3 py-2 font-mono text-[11px] text-tertiary overflow-auto">
          <div>
            <span className="text-secondary">action:</span> {event.action}
          </div>
          <div>
            <span className="text-secondary">target:</span> {event.target_type}/{event.target_id || "—"}
          </div>
          {event.diff !== undefined && event.diff !== null && (
            <details className="mt-1" open>
              <summary className="cursor-pointer text-secondary">diff</summary>
              <pre className="whitespace-pre-wrap break-all m-0 mt-1">
                {JSON.stringify(event.diff, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Map an `action` string to a human verb + object phrase. Falls back to the
 * raw action when an unknown one shows up — better than hiding new events.
 */
function describeEvent(e: AuditEvent): { verb: string; object: string; tone: string } {
  const action = e.action;
  const targetId = e.target_id || "";
  const tag = (id: string) => (id ? id.slice(-6) : "");

  // Known events. Add new ones here as they appear in the hub's audit() calls.
  const KNOWN: Record<string, { verb: string; object: string; tone: string }> = {
    "user.signup": { verb: "signed up", object: "", tone: "bg-status-running" },
    "user.login": { verb: "logged in", object: "", tone: "bg-tertiary" },
    "user.logout_everywhere": {
      verb: "signed out everywhere",
      object: "",
      tone: "bg-tertiary",
    },
    "user.password_changed": {
      verb: "changed their password",
      object: "",
      tone: "bg-status-running",
    },
    "admin.user_created": {
      verb: "added a user",
      object: tag(targetId),
      tone: "bg-status-running",
    },
    "admin.user_disabled": {
      verb: "disabled user",
      object: tag(targetId),
      tone: "bg-status-error",
    },
    "admin.user_enabled": {
      verb: "re-enabled user",
      object: tag(targetId),
      tone: "bg-status-running",
    },
    "admin.user_updated": {
      verb: "updated user",
      object: tag(targetId),
      tone: "bg-status-running",
    },
    "device.pair_init": {
      verb: "started pairing",
      object: tag(targetId),
      tone: "bg-tertiary",
    },
    "device.pair_complete": {
      verb: "paired",
      object: `device ${tag(targetId)}`,
      tone: "bg-status-running",
    },
    "device.revoke": {
      verb: "revoked device",
      object: tag(targetId),
      tone: "bg-status-error",
    },
    "agent_host.update": {
      verb: "reassigned agent host",
      object: tag(targetId),
      tone: "bg-accent",
    },
  };

  if (KNOWN[action]) return KNOWN[action];

  // Generic fallback: "verb the object".
  const [domain, rest] = action.split(".", 2);
  return {
    verb: rest ? rest.replace(/_/g, " ") : action,
    object: `${domain}${targetId ? ` ${tag(targetId)}` : ""}`,
    tone: "bg-tertiary",
  };
}
